// src/views/auth/ActivationStudentView/ActivationStudentView.tsx
import React, {
  FC, useCallback, useEffect, useState,
} from 'react';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Spin, Typography } from 'antd';
import { AxiosError } from 'axios';
import { decode } from 'jsonwebtoken';
import classes from './ActivationStudentView.module.less';
import { isTokenValid } from '../../../common/validators';
import {
  getActivationError, getActivationErrorText, getIsActivatingAccount, getIsActivationLinkRejected,
} from '../slice/selectors';
import logo from '../../../assets/ logo.png';
import useGoToRoute from '../../../hooks/useGoToRoute';
import { Path } from '../../../constants/Path';
import Text from '../../../components/Text';
import AuthErrorMessage from '../../../components/AuthErrorMessage';
import { error, success } from '../../../components/Message';
import useActionStatus from '../../../hooks/useActionStatus';
import ActivationStudentForm from './components/ActivationStudentForm';
import { activateStudentAccount } from '../slice/actions';
import API from '../../../services/ApiService';

const { Title } = Typography;

type LinkStatus = 'checking' | 'valid' | 'invalid';

// Ta sama strona obsługuje link aktywacyjny i link do nowego hasła. Rodzaj zapisuje backend w tokenie;
// token bez tego pola to stary link aktywacyjny
const isPasswordLink = (token: string): boolean => {
  const payload = decode(token);

  return typeof payload === 'object' && payload?.purpose === 'password';
};

// Teksty, które przy nowym haśle byłyby nieprawdą („aktywuj konto”)
const TEXT_KEYS = {
  activation: {
    title: 'common:passwordStrength.title',
    submit: 'common:activation.labels.submit',
    invalidLink: 'common:activation.messages.invalidLinkStudent',
    success: 'common:signUp.messages.activationSuccess',
    error: 'common:signUp.messages.activationError',
    genericError: 'common:activation.messages.genericError',
  },
  password: {
    title: 'common:newPassword.title',
    submit: 'common:newPassword.submit',
    invalidLink: 'common:newPassword.messages.invalidLink',
    success: 'common:newPassword.messages.success',
    error: 'common:newPassword.messages.error',
    genericError: 'common:newPassword.messages.genericError',
  },
};

const ActivationStudentView: FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const goToLogin = useGoToRoute(Path.LOGIN);
  const texts = TEXT_KEYS[isPasswordLink(token) ? 'password' : 'activation'];
  // Wygasły albo nieczytelny link front rozpoznaje sam. Link z ważną datą mógł zostać zastąpiony nowszym
  // albo już zużyty — o to pytamy serwer, zanim uczeń na darmo wpisze hasło
  const [linkStatus, setLinkStatus] = useState<LinkStatus>(() => (isTokenValid(token) ? 'checking' : 'invalid'));

  useEffect(() => {
    if (!isTokenValid(token)) {
      return () => {};
    }

    let isCurrent = true;

    API.user.checkStudentActivation(token)
      .then(() => {
        if (isCurrent) {
          setLinkStatus('valid');
        }
      })
      .catch((e: AxiosError) => {
        // Tylko 400 znaczy „link nie działa”. Brak sieci czy awaria serwera nie może blokować aktywacji:
        // wtedy formularz jak dawniej, a link i tak sprawdzi sama aktywacja
        if (isCurrent) {
          setLinkStatus(e.response?.status === 400 ? 'invalid' : 'valid');
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [token]);

  const errorMessage = useSelector(getActivationError);
  const errorText = useSelector(getActivationErrorText);
  const isLinkRejected = useSelector(getIsActivationLinkRejected);

  const onSuccess = useCallback(() => {
    success(t(texts.success));
    goToLogin();
  }, [goToLogin, t, texts]);

  // Po błędzie zostajemy na stronie: powód i przejście do logowania są pod formularzem
  const onError = useCallback(() => {
    error(t(texts.error));
  }, [t, texts]);

  useActionStatus(onSuccess, onError, {
    isLoadingSelector: getIsActivatingAccount,
    errorSelector: getActivationError,
  });

  const onSubmit = useCallback((values) => (
    token && dispatch(activateStudentAccount(token, values.password))
  ), [dispatch, token]);

  const goToLoginButton = (
    <Button size="large" onClick={goToLogin} className={classes.backButton} style={{ marginTop: 24 }}>
      {t('common:activation.labels.goToLogin')}
    </Button>
  );

  // Wygasły, zastąpiony nowszym albo zużyty link — zamiast formularza wyjaśnienie. Tekst jest ten sam,
  // który serwer zwraca przy 400, więc ekran nie zależy od tego, kto link odrzucił
  if (linkStatus === 'invalid') {
    return (
      <div className={classes.container}>
        <img className={classes.logo} src={logo} alt="Logo" />
        <div className={classes.frame}>
          <Text className={classes.message}>
            {t(texts.invalidLink)}
          </Text>
          {goToLoginButton}
        </div>
      </div>
    );
  }

  // Formularz dopiero po sprawdzeniu linku, żeby nie mignął przed komunikatem
  if (linkStatus === 'checking') {
    return (
      <div className={classes.container}>
        <img className={classes.logo} src={logo} alt="Logo" />
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <img className={classes.logo} src={logo} alt="Logo" />
      <div className={classes.frame}>
        <Title className={classes.title}>{t(texts.title)}</Title>
        <Text className={classes.subtitle}>{t('common:passwordStrength.description')}</Text>
        <ActivationStudentForm onSubmit={onSubmit} submitLabel={t(texts.submit)} />
        {Boolean(errorMessage) && (
          <>
            <AuthErrorMessage>{errorText ?? t(isLinkRejected ? texts.invalidLink : texts.genericError)}</AuthErrorMessage>
            {goToLoginButton}
          </>
        )}
      </div>
    </div>
  );
};

export default ActivationStudentView;
